// ── Enhanced Muzzle Flash ──

import * as THREE from 'three';
import { addEffect, createSpriteTexture, markEffect, disposeMesh, rand } from './effect-loop';

// ---------------------------------------------------------------------------
// Muzzle Flash PointLight Pool
// ---------------------------------------------------------------------------
// Pre-creates PointLight instances to avoid shader recompilation when
// adding/removing lights from the scene. Lights stay in the scene at
// intensity 0 when inactive.

export class MuzzleFlashLightPool {
  private lights: THREE.PointLight[] = [];
  private active: Set<THREE.PointLight> = new Set();

  constructor(scene: THREE.Scene, poolSize: number = 4) {
    for (let i = 0; i < poolSize; i++) {
      const light = new THREE.PointLight(0xffa830, 0, 8, 2);
      light.visible = false;
      markEffect(light);
      scene.add(light);
      this.lights.push(light);
    }
  }

  /**
   * Acquire an inactive light from the pool and set its position + intensity.
   * Returns null if all lights are in use.
   */
  acquire(position: THREE.Vector3): THREE.PointLight | null {
    for (const light of this.lights) {
      if (!this.active.has(light)) {
        this.active.add(light);
        light.position.copy(position);
        light.color.setHex(0xffa830);
        light.intensity = 5;
        light.distance = 8;
        light.decay = 2;
        light.visible = true;
        return light;
      }
    }
    return null; // pool exhausted
  }

  /**
   * Release a light back to the pool by setting intensity to 0 and hiding it.
   */
  release(light: THREE.PointLight): void {
    light.intensity = 0;
    light.visible = false;
    this.active.delete(light);
  }

  /**
   * Dispose all pooled lights and remove them from the scene.
   */
  dispose(scene: THREE.Scene): void {
    for (const light of this.lights) {
      scene.remove(light);
      light.dispose();
    }
    this.lights.length = 0;
    this.active.clear();
  }
}

// Module-level pool instance (lazy init on first use)
let _lightPool: MuzzleFlashLightPool | null = null;
let _poolScene: THREE.Scene | null = null;

function getLightPool(scene: THREE.Scene): MuzzleFlashLightPool {
  if (!_lightPool || _poolScene !== scene) {
    // If scene changed, dispose old pool
    if (_lightPool && _poolScene) {
      _lightPool.dispose(_poolScene);
    }
    _lightPool = new MuzzleFlashLightPool(scene);
    _poolScene = scene;
  }
  return _lightPool;
}

/**
 * Dispose the module-level muzzle flash light pool. Call on game dispose so
 * pooled THREE.PointLight instances are released with the old scene.
 */
export function disposeEnhancedMuzzle(): void {
  if (_lightPool && _poolScene) {
    _lightPool.dispose(_poolScene);
  }
  _lightPool = null;
  _poolScene = null;
  _lastEnhancedMuzzleTime = 0;
}

// ---------------------------------------------------------------------------
// Effect throttling
// ---------------------------------------------------------------------------

let _lastEnhancedMuzzleTime = 0;
const ENHANCED_MUZZLE_MIN_INTERVAL = 80; // ms — skip if firing faster

// ---------------------------------------------------------------------------
// 1. Enhanced Muzzle Flash
// ---------------------------------------------------------------------------

export function spawnEnhancedMuzzleFlash(
  position: THREE.Vector3,
  direction: THREE.Vector3,
  scene: THREE.Scene,
): void {
  // Throttle: skip if firing too fast
  const now = performance.now();
  if (now - _lastEnhancedMuzzleTime < ENHANCED_MUZZLE_MIN_INTERVAL) return;
  _lastEnhancedMuzzleTime = now;

  // --- Point light from pool (avoids shader recompilation) ---
  const pool = getLightPool(scene);
  const light = pool.acquire(position);

  if (light) {
    const lightStart = now;
    const LIGHT_DURATION = 40;

    addEffect({
      update(now) {
        const t = (now - lightStart) / LIGHT_DURATION;
        if (t >= 1) {
          pool.release(light);
          return false;
        }
        light.intensity = 5 * (1 - t);
        return true;
      },
    });
  }

  // --- Flash sprites (2-3 billboards, reduced from 3-5) ---
  const flashTex = createSpriteTexture(64, 'rgba(255,255,220,1)', 'rgba(255,160,30,0.8)', 'circle');
  const spriteCount = 2 + Math.floor(Math.random() * 2);
  const sprites: Array<{ sprite: THREE.Sprite; velocity: THREE.Vector3 }> = [];

  for (let i = 0; i < spriteCount; i++) {
    const material = new THREE.SpriteMaterial({
      map: flashTex,
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: 1,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(0.15, 0.15, 0.15);
    sprite.position.copy(position);
    markEffect(sprite);
    scene.add(sprite);

    const vel = direction.clone().multiplyScalar(rand(3, 6));
    vel.x += rand(-1.5, 1.5);
    vel.y += rand(-1.0, 1.5);
    vel.z += rand(-1.5, 1.5);

    sprites.push({ sprite, velocity: vel });
  }

  const flashStart = now;
  const FLASH_DURATION = 80;

  addEffect({
    update(now) {
      const elapsed = now - flashStart;
      const t = Math.min(elapsed / FLASH_DURATION, 1);

      for (const s of sprites) {
        s.sprite.position.addScaledVector(s.velocity, 0.016);
        s.velocity.multiplyScalar(0.9);
        const mat = s.sprite.material as THREE.SpriteMaterial;
        mat.opacity = 1 - t;
        mat.color.setRGB(1, 1 - t * 0.5, 1 - t);
        const scale = 0.15 * (1 - t * 0.7);
        s.sprite.scale.set(scale, scale, scale);
      }

      if (t >= 1) {
        for (const s of sprites) disposeMesh(s.sprite, scene);
        return false;
      }
      return true;
    },
  });

  // --- Smoke puff after flash (reduced to 1-2) ---
  const smokeTex = createSpriteTexture(64, 'rgba(160,155,145,0.6)', 'rgba(120,115,105,0.2)', 'smoke');
  const smokeCount = 1 + Math.floor(Math.random() * 2);
  const smokePuffs: Array<{ sprite: THREE.Sprite; velocity: THREE.Vector3 }> = [];

  for (let i = 0; i < smokeCount; i++) {
    const material = new THREE.SpriteMaterial({
      map: smokeTex,
      transparent: true,
      opacity: 0.4,
      depthWrite: false,
    });
    const sprite = new THREE.Sprite(material);
    sprite.scale.set(0.08, 0.08, 0.08);
    sprite.position.copy(position).addScaledVector(direction, 0.1);
    markEffect(sprite);
    scene.add(sprite);

    smokePuffs.push({
      sprite,
      velocity: new THREE.Vector3(
        rand(-0.3, 0.3) + direction.x * 0.5,
        rand(0.3, 0.8),
        rand(-0.3, 0.3) + direction.z * 0.5,
      ),
    });
  }

  const smokeStart = now;
  const SMOKE_DURATION = 400;

  addEffect({
    update(now) {
      const elapsed = now - smokeStart;
      const t = Math.min(elapsed / SMOKE_DURATION, 1);

      for (const s of smokePuffs) {
        s.sprite.position.addScaledVector(s.velocity, 0.016);
        s.velocity.multiplyScalar(0.97);
        const mat = s.sprite.material as THREE.SpriteMaterial;
        mat.opacity = 0.4 * (1 - t);
        const scale = 0.08 + t * 0.15;
        s.sprite.scale.set(scale, scale, scale);
      }

      if (t >= 1) {
        for (const s of smokePuffs) disposeMesh(s.sprite, scene);
        return false;
      }
      return true;
    },
  });
}
